// Product DAO — data access for products.
//
// Minimal surface: extends BaseDao so the generic CRUD (`getById`,
// `create`, `update`, `delete`, `updateMany`) is inherited. Add custom
// queries here as use cases arise; the FE entity tree currently reads
// products via Refine's data provider, so we don't duplicate listing
// logic here.

import { BaseDao, type SupabaseClient, type InvalidateFn } from '../base.ts';
import type { Product } from '@shared/types/product.ts';

export class ProductDao extends BaseDao<Product> {
  protected readonly dependentResources = ['products'];

  constructor(sb: SupabaseClient, invalidate?: InvalidateFn) {
    super(sb, 'products', invalidate);
  }

  /**
   * Resolve waste-code strings to the product IDs that carry them.
   * Used by the transactions filter to turn a waste-code multi-select
   * into a `product_id in (...)` PostgREST filter.
   */
  async idsByWasteCodes(wasteCodes: string[]): Promise<number[]> {
    if (!wasteCodes.length) return [];
    const { data, error } = await this.sb
      .from('products')
      .select('id')
      .in('waste_code', wasteCodes)
      .eq('is_deleted', false);
    if (error) throw error;
    return (data ?? []).map((p: { id: number }) => p.id);
  }
}
